import frontendLogger from "../utils/frontendLogger.js";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../app_config.js";
import "../pages/css/App.css";
import "../pages/css/Print.css";
import copyStylesheets from "../utils/copyStylesheets.js";

function Register() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteToken, setInviteToken] = useState("");
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteChecked, setInviteChecked] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const navigate = useNavigate();
  const isInviteReady = inviteChecked && inviteValid;

  const validateForm = () => {
    if (username.length < 3) {
      setError("Benutzername muss mindestens 3 Zeichen lang sein");
      return false;
    }
    if (password.length < 6) {
      setError("Passwort muss mindestens 6 Zeichen lang sein");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Passwörter stimmen nicht überein");
      return false;
    }
    return true;
  };

  const printCredentials = async () => {
    const printWindow = window.open("", "", "width=400,height=400");
    if (!printWindow) return;

    const doc = printWindow.document;
    doc.title = "Login Daten drucken";

    const container = doc.createElement("div");
    container.className = "credentials-print-container";

    const heading = doc.createElement("h2");
    heading.textContent = "Registrierungsdaten DRAM-App";
    container.appendChild(heading);

    const userPara = doc.createElement("p");
    userPara.innerHTML = `<strong>Username:</strong> ${username}`;
    container.appendChild(userPara);

    const passPara = doc.createElement("p");
    passPara.innerHTML = `<strong>Password:</strong> ${password}`;
    container.appendChild(passPara);

    const footer = doc.createElement("div");
    footer.className = "print-footer";
    footer.textContent = "DRAM-App | Sicher aufbewahren";
    container.appendChild(footer);

    doc.body.appendChild(container);

    try {
      await copyStylesheets(document, doc);
      printWindow.focus();
      printWindow.print();
    } catch (err) {
      frontendLogger.error("Print stylesheet loading error:", err);
      window.alert("Das Druck-Stylesheet konnte nicht geladen werden.");
    }
  };

  const showRecoveryCodesPopup = async (codes) => {
    const popup = window.open("", "", "width=500,height=500");
    if (!popup) return;

    const doc = popup.document;
    doc.title = "Recovery Codes";

    const container = doc.createElement("div");
    container.className = "credentials-print-container";

    const heading = doc.createElement("h2");
    heading.textContent = "Recovery Codes DRAM-App";
    container.appendChild(heading);

    const info = doc.createElement("p");
    info.textContent =
      "Diese Codes bitte sicher speichern. Jeder Code ist nur einmal gültig.";
    container.appendChild(info);

    const list = doc.createElement("ul");

    codes.forEach((code) => {
      const li = doc.createElement("li");
      li.textContent = code;
      list.appendChild(li);
    });

    container.appendChild(list);
    const printButton = doc.createElement("button");
    printButton.textContent = "Codes Drucken";
    printButton.style.marginTop = "20px";
    printButton.disabled = true;

    printButton.onclick = () => {
      popup.focus();
      popup.print();
    };
    container.appendChild(printButton);

    const footer = doc.createElement("div");
    footer.className = "print-footer";
    footer.textContent = "DRAM-App | Sicher aufbewahren";
    container.appendChild(footer);

    doc.body.appendChild(container);

    try {
      await copyStylesheets(document, doc);
      printButton.disabled = false;
    } catch (err) {
      frontendLogger.error("Print stylesheet loading error:", err);
      window.alert("Das Druck-Stylesheet konnte nicht geladen werden.");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    if (!inviteValid) {
      setError("Registrierung nur mit einem gültigen Einladung-Link möglich.");
      return;
    }

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password, inviteToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Registrierung fehlgeschlagen");
      } else {
        setSuccess("Registrierung erfolgreich!");

        const codes = data.recoveryCodes || [];

        // Popup sofort anzeigen (nicht über State notwendig)
        if (codes.length > 0) {
          showRecoveryCodesPopup(codes);
        }

        setUsername("");
        setPassword("");
        setConfirmPassword("");

        // optional: Navigation NICHT sofort
        // navigate("/login");
      }
    } catch (err) {
      setError("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
      frontendLogger.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("invite");

    if (!token) {
      setInviteError(
        "Registrierung ist nur über einen Einladung-Link möglich.",
      );
      setInviteChecked(true);
      return;
    }

    const validateInvite = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/auth/invite/validate?token=${encodeURIComponent(
            token,
          )}`,
        );
        const data = await response.json();

        if (!response.ok) {
          setInviteError(data.message || "Ungültiger Einladung-Link.");
          setInviteValid(false);
        } else {
          setInviteValid(true);
          setInviteError("");
          setInviteToken(token);
        }
      } catch (err) {
        setInviteError("Fehler beim Überprüfen des Einladung-Links.");
        frontendLogger.error("Invite validation error:", err);
      } finally {
        setInviteChecked(true);
      }
    };

    validateInvite();
  }, []);

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>DRAM Registration</h1>
        <h3>Digital Review and Aroma Manager</h3>

        <form onSubmit={handleRegister}>
          {!inviteChecked && (
            <div className="info-message">Einladung wird geprüft...</div>
          )}
          {inviteError && <div className="error-message">{inviteError}</div>}
          <div className="form-group">
            <label>Benutzername</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={!isInviteReady}
            />
          </div>

          <div className="form-group">
            <label>Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={!isInviteReady}
            />
          </div>

          <div className="form-group">
            <label>Passwort bestätigen</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={!isInviteReady}
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          <div className="nav">
            <button type="submit" disabled={loading || !isInviteReady}>
              {loading ? "Registrierung..." : "Registrieren"}
            </button>
            <button
              type="button"
              onClick={printCredentials}
              disabled={!username || !password}
            >
              Logindaten drucken
            </button>
          </div>
        </form>

        <div className="form-footer">
          <p>Du hast bereits ein Konto?</p>
          <button
            type="button"
            className="switch-button"
            onClick={() => navigate("/login")}
          >
            Login here
          </button>{" "}
          <p style={{ marginTop: "14px" }}>
            <Link className="privacy-link" to="/privacy">
              Datenschutzerklärung
            </Link>
          </p>{" "}
        </div>
      </div>
    </div>
  );
}

export default Register;
